import type { Column } from './types';

/**
 * ส่งออกข้อมูลตารางเป็น CSV หรือ Excel
 *
 * CSV: ใส่ BOM ของ UTF-8 เพราะ Excel บน Windows อ่านภาษาไทยเป็นขยะถ้าไม่มี BOM
 * Excel: ใช้ SpreadsheetML (.xls) ที่ Excel เปิดได้โดยไม่ต้องมี library
 *        — ถ้าต้องการ .xlsx จริงให้ทำที่ฝั่ง backend ตาม FR-RPT-10
 *
 * การส่งออกข้อมูลลูกค้าต้องบันทึก audit log ทุกครั้ง (FR-ADM-39) —
 * หน้าจอที่เรียกฟังก์ชันนี้ต้องยิง audit เอง ฟังก์ชันนี้ไม่รู้บริบทว่าใครกด
 */

export type ExportFormat = 'csv' | 'excel';

function cell<Row>(col: Column<Row>, row: Row): string {
  const v = col.value?.(row);
  if (v === null || v === undefined) return '';
  return String(v);
}

function download(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const escapeXml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

export function exportRows<Row>(opts: {
  columns: Array<Column<Row>>;
  rows: Row[];
  /** ชื่อไฟล์โดยไม่ต้องมีนามสกุล เช่น 'คิวคำขอสะสมคะแนน-2569-08-21' */
  filename: string;
  format: ExportFormat;
  /** id ของคอลัมน์ที่จะส่งออก — ไม่ระบุ = ทุกคอลัมน์ที่มี value() */
  columnIds?: string[];
}): number {
  const { columns, rows, filename, format, columnIds } = opts;
  const cols = (columnIds ? columnIds.map((id) => columns.find((c) => c.id === id)!) : columns)
    .filter((c) => c && typeof c.value === 'function');

  if (format === 'csv') {
    const head = cols.map((c) => `"${c.header.replace(/"/g, '""')}"`).join(',');
    const body = rows
      .map((r) => cols.map((c) => `"${cell(c, r).replace(/"/g, '""')}"`).join(','))
      .join('\r\n');
    download(new Blob(['\uFEFF' + head + '\r\n' + body], { type: 'text/csv;charset=utf-8' }), `${filename}.csv`);
    return rows.length;
  }

  const head = cols.map((c) => `<Cell><Data ss:Type="String">${escapeXml(c.header)}</Data></Cell>`).join('');
  const body = rows
    .map((r) => {
      const tds = cols
        .map((c) => {
          const raw = c.value!(r);
          const numeric = typeof raw === 'number';
          return `<Cell><Data ss:Type="${numeric ? 'Number' : 'String'}">${escapeXml(String(raw ?? ''))}</Data></Cell>`;
        })
        .join('');
      return `<Row>${tds}</Row>`;
    })
    .join('');

  const xml = `<?xml version="1.0" encoding="UTF-8"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
<Worksheet ss:Name="ข้อมูล"><Table><Row>${head}</Row>${body}</Table></Worksheet>
</Workbook>`;
  download(new Blob([xml], { type: 'application/vnd.ms-excel;charset=utf-8' }), `${filename}.xls`);
  return rows.length;
}

/** ชื่อไฟล์ที่ลงท้ายด้วยวันที่ พ.ศ. */
export function exportFilename(base: string): string {
  const d = new Date();
  const y = d.getFullYear() + 543;
  return `${base}-${y}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
}
